import React, { useMemo, useState } from 'react';
import {
  Lightbulb,
  Settings,
  Link2,
  Package,
  FileText,
  ChevronDown,
  ChevronRight,
  Filter
} from 'lucide-react';
import { PRODUCT_SUGGESTIONS, getNextLevel, CISA_FUNCTIONS } from '../../config/cisaMaturityModel';
import RecommendationCard from '../components/RecommendationCard';

/**
 * PillarRecommendationsTab - Prioritized recommendations in four tiers
 *
 * Tiers:
 * 1. CONFIGURE_EXISTING - Products adopted but not fully configured (highest priority)
 * 2. INTEGRATE_EXISTING - Missing integrations between adopted products
 * 3. MISSING_TECHNOLOGY - No product for the function
 * 4. GOVERNANCE_ACTION - Policy/process gaps
 */
const PillarRecommendationsTab = ({
  pillarCode,
  pillar,
  functions,
  assessments,
  productAdoptions,
  configCompletions = {},
  derivedScores = {},
  evidence = {},
  evidenceChecks = {},
  onAdoptProduct,
  onViewConfig,
  onViewEvidence
}) => {
  const [expandedTiers, setExpandedTiers] = useState({
    CONFIGURE_EXISTING: true,
    INTEGRATE_EXISTING: true,
    MISSING_TECHNOLOGY: true,
    GOVERNANCE_ACTION: false
  });
  const [priorityFilter, setPriorityFilter] = useState('ALL');

  // Toggle tier expansion
  const toggleTier = (tier) => {
    setExpandedTiers(prev => ({ ...prev, [tier]: !prev[tier] }));
  };

  // Build recommendations organized by tier
  const { recommendations, tierCounts } = useMemo(() => {
    const recs = {
      CONFIGURE_EXISTING: [],
      INTEGRATE_EXISTING: [],
      MISSING_TECHNOLOGY: [],
      GOVERNANCE_ACTION: []
    };

    for (const fn of functions) {
      const currentLevel = assessments[fn.code] || 'TRADITIONAL';
      const nextLevel = getNextLevel(currentLevel);
      const adoptedProducts = productAdoptions[fn.code] || [];
      const fnConfigs = configCompletions[fn.code] || {};
      const fnDerivedScore = derivedScores[fn.code];
      const fnEvidence = evidence[fn.code] || [];

      // Calculate config completion percentage for each product
      for (const product of adoptedProducts) {
        if (product.status === 'DEPLOYED' || product.status === 'EVALUATING') {
          const productConfigs = fnConfigs[product.productName] || {};
          const configSteps = Object.values(productConfigs);
          const completedSteps = configSteps.filter(s => s === true).length;
          const totalSteps = configSteps.length || 1;
          const configPercentage = Math.round((completedSteps / totalSteps) * 100);

          // If config < 100%, add to CONFIGURE_EXISTING
          if (configPercentage < 100 && totalSteps > 0) {
            const pendingSteps = totalSteps - completedSteps;
            recs.CONFIGURE_EXISTING.push({
              id: `config-${fn.code}-${product.productName}`,
              type: 'CONFIGURE_EXISTING',
              priority: configPercentage < 50 ? 'HIGH' : 'MEDIUM',
              title: `Complete ${product.productName} configuration`,
              description: `${pendingSteps} configuration step${pendingSteps !== 1 ? 's' : ''} remaining to enable full capability`,
              functionCode: fn.code,
              functionName: fn.name,
              product: product.productName,
              currentLevel: currentLevel,
              targetLevel: nextLevel,
              maturityImpact: 0.3 * (pendingSteps / totalSteps),
              effort: pendingSteps <= 2 ? 'LOW' : pendingSteps <= 5 ? 'MEDIUM' : 'HIGH',
              actionLabel: 'View Configuration Steps',
              configPercentage
            });
          }
        }
      }

      // Check for missing integrations between products
      // (simplified: if multiple products but low config completeness, suggest integration)
      if (adoptedProducts.length >= 2 && fnDerivedScore?.configurationCompleteness < 60) {
        const productNames = adoptedProducts.map(p => p.productName).slice(0, 2);
        recs.INTEGRATE_EXISTING.push({
          id: `integrate-${fn.code}`,
          type: 'INTEGRATE_EXISTING',
          priority: 'MEDIUM',
          title: `Integrate ${productNames.join(' with ')}`,
          description: `Connect these tools to improve data flow and automate workflows for ${fn.name}`,
          functionCode: fn.code,
          functionName: fn.name,
          product: productNames.join(' + '),
          currentLevel: currentLevel,
          targetLevel: nextLevel,
          maturityImpact: 0.4,
          effort: 'MEDIUM',
          actionLabel: 'View Integration Guide'
        });
      }

      // Check for missing technology
      if (nextLevel && PRODUCT_SUGGESTIONS[fn.code]?.[nextLevel]) {
        const suggestions = PRODUCT_SUGGESTIONS[fn.code][nextLevel];
        const adoptedNames = new Set(adoptedProducts.map(p => p.productName));
        const unadoptedSuggestions = suggestions.filter(s => !adoptedNames.has(s));

        if (unadoptedSuggestions.length > 0 && adoptedProducts.length === 0) {
          // No products at all - high priority
          recs.MISSING_TECHNOLOGY.push({
            id: `missing-${fn.code}`,
            type: 'MISSING_TECHNOLOGY',
            priority: 'HIGH',
            title: `Add technology for ${fn.name}`,
            description: `No products deployed. Consider: ${unadoptedSuggestions.slice(0, 3).join(', ')}`,
            functionCode: fn.code,
            functionName: fn.name,
            product: null,
            currentLevel: currentLevel,
            targetLevel: nextLevel,
            maturityImpact: 0.8,
            effort: 'HIGH',
            actionLabel: 'Browse Products',
            suggestions: unadoptedSuggestions
          });
        } else if (unadoptedSuggestions.length > 0) {
          // Has some products, but missing others for next level
          recs.MISSING_TECHNOLOGY.push({
            id: `missing-${fn.code}`,
            type: 'MISSING_TECHNOLOGY',
            priority: 'LOW',
            title: `Consider additional products for ${fn.name}`,
            description: `To reach ${nextLevel}: ${unadoptedSuggestions.slice(0, 2).join(', ')}`,
            functionCode: fn.code,
            functionName: fn.name,
            product: null,
            currentLevel: currentLevel,
            targetLevel: nextLevel,
            maturityImpact: 0.5,
            effort: 'HIGH',
            actionLabel: 'Browse Products',
            suggestions: unadoptedSuggestions
          });
        }
      }

      // Check for governance gaps (missing evidence)
      if (fnDerivedScore?.evidenceConfidence < 50 && adoptedProducts.length > 0) {
        recs.GOVERNANCE_ACTION.push({
          id: `governance-${fn.code}`,
          type: 'GOVERNANCE_ACTION',
          priority: fnEvidence.length === 0 ? 'HIGH' : 'MEDIUM',
          title: `Document ${fn.name} implementation`,
          description: 'Upload evidence artifacts to validate current maturity level and improve audit readiness',
          functionCode: fn.code,
          functionName: fn.name,
          product: null,
          currentLevel: currentLevel,
          targetLevel: null,
          maturityImpact: 0.2,
          effort: 'LOW',
          actionLabel: 'Add Evidence'
        });
      }
    }

    // Sort each tier by priority
    const priorityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
    for (const tier of Object.keys(recs)) {
      recs[tier].sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
    }

    const counts = {
      CONFIGURE_EXISTING: recs.CONFIGURE_EXISTING.length,
      INTEGRATE_EXISTING: recs.INTEGRATE_EXISTING.length,
      MISSING_TECHNOLOGY: recs.MISSING_TECHNOLOGY.length,
      GOVERNANCE_ACTION: recs.GOVERNANCE_ACTION.length,
      total: Object.values(recs).reduce((sum, arr) => sum + arr.length, 0)
    };

    return { recommendations: recs, tierCounts: counts };
  }, [functions, assessments, productAdoptions, configCompletions, derivedScores, evidence]);

  // Filter recommendations by priority
  const filterRecs = (recs) => {
    if (priorityFilter === 'ALL') return recs;
    return recs.filter(r => r.priority === priorityFilter);
  };

  // Handle recommendation action
  const handleAction = (rec) => {
    switch (rec.type) {
      case 'CONFIGURE_EXISTING':
        onViewConfig?.(rec.functionCode, rec.product);
        break;
      case 'INTEGRATE_EXISTING':
        onViewConfig?.(rec.functionCode);
        break;
      case 'MISSING_TECHNOLOGY':
        // Could open a product browser/add modal
        if (rec.suggestions?.length > 0) {
          onAdoptProduct?.(rec.functionCode, rec.suggestions[0], 'EVALUATING');
        }
        break;
      case 'GOVERNANCE_ACTION':
        onViewEvidence?.(rec.functionCode);
        break;
      default:
        break;
    }
  };

  const tierConfig = {
    CONFIGURE_EXISTING: {
      icon: Settings,
      title: 'Configure What You Own',
      subtitle: 'Products deployed but not fully configured',
      color: '#8b5cf6'
    },
    INTEGRATE_EXISTING: {
      icon: Link2,
      title: 'Integrate Existing Tools',
      subtitle: 'Connect your tools for better automation',
      color: '#3b82f6'
    },
    MISSING_TECHNOLOGY: {
      icon: Package,
      title: 'Missing Technology',
      subtitle: 'Functions without adequate product coverage',
      color: '#f59e0b'
    },
    GOVERNANCE_ACTION: {
      icon: FileText,
      title: 'Governance Actions',
      subtitle: 'Documentation and evidence gaps',
      color: '#10b981'
    }
  };

  const tierOrder = ['CONFIGURE_EXISTING', 'INTEGRATE_EXISTING', 'MISSING_TECHNOLOGY', 'GOVERNANCE_ACTION'];

  return (
    <div className="cisa-pillar-recommendations">
      {/* Header */}
      <div className="cisa-pillar-recommendations-header">
        <Lightbulb size={20} style={{ color: pillar.color }} />
        <div>
          <h3>Prioritized Recommendations</h3>
          <p>Actions to advance your maturity level, ordered by impact</p>
        </div>
      </div>

      {/* Priority Filter */}
      <div className="cisa-pillar-recommendations-filters">
        <Filter size={14} />
        <span>Priority:</span>
        {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(p => (
          <button
            key={p}
            type="button"
            className={`cisa-pillar-recommendations-filter-btn ${priorityFilter === p ? 'active' : ''}`}
            onClick={() => setPriorityFilter(p)}
          >
            {p === 'ALL' ? 'All' : p.charAt(0) + p.slice(1).toLowerCase()}
          </button>
        ))}
        <span className="cisa-pillar-recommendations-total">
          {tierCounts.total} recommendation{tierCounts.total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Tier sections */}
      {tierOrder.map(tier => {
        const config = tierConfig[tier];
        const Icon = config.icon;
        const tierRecs = filterRecs(recommendations[tier]);
        const count = tierCounts[tier];

        if (count === 0) return null;

        return (
          <div key={tier} className="cisa-pillar-recommendations-tier">
            {/* Tier header */}
            <button
              type="button"
              className="cisa-pillar-recommendations-tier-header"
              onClick={() => toggleTier(tier)}
              style={{ '--tier-color': config.color }}
            >
              <div className="cisa-pillar-recommendations-tier-left">
                {expandedTiers[tier] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <div className="cisa-pillar-recommendations-tier-icon" style={{ background: `${config.color}20`, color: config.color }}>
                  <Icon size={16} />
                </div>
                <div className="cisa-pillar-recommendations-tier-info">
                  <span className="cisa-pillar-recommendations-tier-title">{config.title}</span>
                  <span className="cisa-pillar-recommendations-tier-subtitle">{config.subtitle}</span>
                </div>
              </div>
              <span className="cisa-pillar-recommendations-tier-count" style={{ background: `${config.color}20`, color: config.color }}>
                {count}
              </span>
            </button>

            {/* Tier content */}
            {expandedTiers[tier] && (
              <div className="cisa-pillar-recommendations-tier-content">
                {tierRecs.map(rec => (
                  <RecommendationCard
                    key={rec.id}
                    type={rec.type}
                    priority={rec.priority}
                    title={rec.title}
                    description={rec.description}
                    maturityImpact={rec.maturityImpact}
                    effort={rec.effort}
                    product={rec.product}
                    functionCode={rec.functionCode}
                    functionName={rec.functionName}
                    currentLevel={rec.currentLevel}
                    targetLevel={rec.targetLevel}
                    actionLabel={rec.actionLabel}
                    onAction={() => handleAction(rec)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state */}
      {tierCounts.total === 0 && (
        <div className="cisa-pillar-recommendations-empty">
          <Lightbulb size={32} />
          <h4>All Caught Up!</h4>
          <p>
            All products are fully configured and you've reached Optimal maturity
            or there are no more recommendations for this pillar.
          </p>
        </div>
      )}
    </div>
  );
};

export default PillarRecommendationsTab;
